import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Info } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SegmentedControl, type SegmentedOption } from '@/components/ui/segmented-control'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import { formatDurationMinutes } from '@/lib/formatDuration'
import { cn } from '@/lib/utils'
import { postRecoveryApi, type LessonAccuracy, type LessonTiming } from './api'
import { formatDate } from '@/lib/dateUtils'

/**
 * The last thing an engineer sees at the end of a runbook.
 *
 * TICKS FIRST, PROSE LAST, AND NOTHING REQUIRED. This is asked of people who have just finished a
 * recovery and want to go home; a form that demands a written answer gets no answer at all, and a
 * form that cannot be dismissed gets a junk one. Two questions of three choices each, then two
 * optional fields, then a plainly labelled way out.
 *
 * An RTO is ELAPSED time, so it is rendered with formatDurationMinutes and never with
 * formatEffortMinutes — the person-time formatter deliberately refuses to roll into days, which is
 * exactly wrong for a number that answers "how long was the outage".
 */

type Draft = {
  accuracy: LessonAccuracy | ''
  timing: LessonTiming | ''
  /** Held as the typed string so an in-progress "1" is not read as one minute. */
  rto: string
  comment: string
}

const EMPTY_DRAFT: Draft = { accuracy: '', timing: '', rto: '', comment: '' }

const ACCURACY_OPTIONS: { value: LessonAccuracy; key: string; fallback: string }[] = [
  { value: 'accurate', key: 'postRecovery.lessonAccuracyAccurate', fallback: 'Accurate' },
  { value: 'minor_issues', key: 'postRecovery.lessonAccuracyMinor', fallback: 'Minor issues' },
  { value: 'significant_issues', key: 'postRecovery.lessonAccuracySignificant', fallback: 'Significant issues' },
]

const TIMING_OPTIONS: { value: LessonTiming; key: string; fallback: string }[] = [
  { value: 'about_right', key: 'postRecovery.lessonTimingAboutRight', fallback: 'About right' },
  { value: 'too_optimistic', key: 'postRecovery.lessonTimingOptimistic', fallback: 'Too optimistic' },
  { value: 'too_pessimistic', key: 'postRecovery.lessonTimingPessimistic', fallback: 'Too pessimistic' },
]

/**
 * One question. The empty answer is a real state — value `''` matches no segment, so an
 * unanswered question renders as a flat track rather than quietly claiming the first choice.
 */
function Question<T extends string>({ label, hint, value, options, onPick }: {
  label: string
  hint?: string
  value: T | ''
  options: SegmentedOption<T | ''>[]
  onPick: (value: T | '') => void
}) {
  return (
    <div>
      <div className="text-sm font-medium">{label}</div>
      {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
      {/* The control is the widest thing in here; it scrolls in its own track so a narrow window
          never makes the dialog itself scroll sideways. */}
      <div className="mt-1.5 overflow-x-auto pb-0.5">
        <SegmentedControl<T | ''>
          size="sm"
          ariaLabel={label}
          value={value}
          options={options}
          // Re-picking the chosen answer clears it. Every answer is optional, so a mis-tick has to
          // be undoable — otherwise the first stray click becomes a permanent opinion.
          onValueChange={(v) => onPick(v === value ? '' : v)}
        />
      </div>
    </div>
  )
}

export function RunbookLessonDialog({
  requestId, objectId, open, onOpenChange, suggestedRTOMinutes,
}: {
  requestId: number | null
  objectId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  suggestedRTOMinutes?: number | null
}) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const initial = useRef<Draft>(EMPTY_DRAFT)
  const hydratedFor = useRef<string | null>(null)

  const lesson = useQuery({
    queryKey: ['post-recovery', 'lesson', requestId],
    queryFn: () => postRecoveryApi.getLesson(requestId as number),
    enabled: open && requestId != null,
  })
  const existing = lesson.data ?? null

  /**
   * What has ever been measured on this object, so the suggested number can be shown with the
   * evidence it rests on. Nothing here blocks the form: the dialog is fully usable while this is
   * in flight, or if it never arrives at all.
   */
  const history = useQuery({
    queryKey: ['post-recovery', 'object-history', objectId],
    queryFn: () => postRecoveryApi.objectHistory(objectId as string),
    enabled: open && !!objectId,
    staleTime: 60_000,
  })

  useEffect(() => {
    // Closing forgets what was hydrated, so the next open re-reads rather than showing the
    // previous run's answers against this one.
    if (!open) { hydratedFor.current = null; return }
    if (requestId == null) return
    // Seeding from an empty result and correcting it when the request lands is how a form eats
    // an answer someone has already started typing.
    if (lesson.isPending) return
    const stamp = String(requestId)
    if (hydratedFor.current === stamp) return
    hydratedFor.current = stamp
    const seeded: Draft = {
      accuracy: existing?.accuracy ?? '',
      timing: existing?.timing ?? '',
      // Their own earlier number beats the suggestion: the suggestion is arithmetic over a handful
      // of measurements, that was a judgement someone made with the recovery in front of them.
      rto: existing?.proposedRTOMinutes != null
        ? String(existing.proposedRTOMinutes)
        : suggestedRTOMinutes != null ? String(Math.round(suggestedRTOMinutes)) : '',
      comment: existing?.comment ?? '',
    }
    initial.current = seeded
    setDraft(seeded)
  }, [open, requestId, lesson.isPending, existing, suggestedRTOMinutes])

  const evidence = useMemo(() => {
    const h = history.data
    if (!h) return null
    return {
      observations: h.tasks.reduce((a, r) => a + r.observations, 0),
      outliers: h.tasks.reduce((a, r) => a + r.outlierObservations, 0),
      runCount: h.runCount,
      basedOnTasks: h.proposal.basedOnTasks,
      coverage: h.coverage,
    }
  }, [history.data])

  const rtoRaw = draft.rto.trim()
  const rtoValue = rtoRaw === '' ? null : Number(rtoRaw)
  const rtoInvalid = rtoValue !== null
    && (!Number.isFinite(rtoValue) || !Number.isInteger(rtoValue) || rtoValue <= 0)

  const dirty = draft.accuracy !== initial.current.accuracy
    || draft.timing !== initial.current.timing
    || draft.rto.trim() !== initial.current.rto.trim()
    || draft.comment.trim() !== initial.current.comment.trim()

  // One tick and nothing else is a worthwhile answer, so anything at all enables the save. The
  // second clause is for the opposite move: clearing an answer that already exists is also a save.
  const hasSomething = draft.accuracy !== '' || draft.timing !== ''
    || draft.comment.trim() !== '' || rtoRaw !== ''
  const canSave = requestId != null && !rtoInvalid && (hasSomething || (existing != null && dirty))

  const save = useMutation({
    mutationFn: () => postRecoveryApi.saveLesson(requestId as number, {
      // Sent as explicit nulls rather than omitted: the server upserts, and an omitted field is
      // the one thing that could leave a cleared answer standing.
      accuracy: draft.accuracy === '' ? null : draft.accuracy,
      timing: draft.timing === '' ? null : draft.timing,
      proposedRTOMinutes: rtoValue,
      comment: draft.comment.trim() === '' ? null : draft.comment.trim(),
    }),
    onSuccess: () => {
      // The whole 'post-recovery' prefix, not this one key: the answer also changes the object's
      // lessons list and the proposal on its history, and those keys are chosen on other screens.
      queryClient.invalidateQueries({ queryKey: ['post-recovery'] })
      toast({
        title: t('postRecovery.lessonSaved', 'Feedback recorded'),
        description: t('postRecovery.lessonSavedBody',
          'It is attached to this run and to the object, ready for whoever plans the next recovery.'),
      })
      onOpenChange(false)
    },
  })

  const title = existing
    ? t('postRecovery.lessonTitleUpdate', 'Update your feedback')
    : t('postRecovery.lessonTitleNew', 'How did that runbook hold up?')

  /**
   * The suggestion's provenance, stated before anyone can act on it.
   *
   * There is almost nothing measured anywhere in this estate — most runs have two measured tasks
   * out of fifteen — so "not yet meaningful" is the ORDINARY case here, not an error path. It is
   * marked in amber and left on screen rather than hidden, because a suggested RTO with its
   * coverage removed is indistinguishable from one that was actually earned.
   */
  const renderEvidence = () => {
    if (history.isPending && !!objectId) {
      return (
        <p className="text-xs text-muted-foreground">
          {t('postRecovery.lessonEvidenceLoading', 'Checking what has actually been measured on this object…')}
        </p>
      )
    }

    if (!evidence) {
      return suggestedRTOMinutes != null ? (
        <p className="text-xs text-muted-foreground">
          {t('postRecovery.lessonEvidenceSuggestedOnly',
            'Prefilled with the number suggested for this run. Nothing is known here about how this object has been measured before, so treat it as a starting point.')}
        </p>
      ) : null
    }

    const { observations, outliers, coverage, runCount, basedOnTasks } = evidence
    const thin = coverage.withEnoughObservations === 0

    return (
      <div className={cn(
        'space-y-1.5 rounded-lg border p-2.5 text-xs',
        observations === 0 || thin
          ? 'bg-amber-50 dark:bg-amber-950/40'
          : 'bg-sky-50 dark:bg-sky-950/40',
      )}>
        <p className="flex items-start gap-1.5">
          {observations === 0 || thin
            ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
            : <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-500" aria-hidden />}
          <span>
            {observations === 0
              ? t('postRecovery.lessonEvidenceNone',
                  'No measured tasks yet — durations are recorded when an engineer moves a task to Started before completing it. Any number you put here is your judgement, not a measurement.')
              : thin
                ? t('postRecovery.lessonEvidenceThin',
                    '{{obs}} measured durations across {{runs}} runs, and not one task has reached the {{min}} observations needed to mean anything yet. A starting point, not a measurement.',
                    { obs: observations, runs: runCount, min: coverage.minObservations })
                : t('postRecovery.lessonEvidenceOk',
                    'Suggested from {{based}} of {{total}} tasks: {{enough}} have at least {{min}} observations, from {{obs}} measured durations across {{runs}} runs.',
                    { based: basedOnTasks, total: coverage.taskCount, enough: coverage.withEnoughObservations, min: coverage.minObservations, obs: observations, runs: runCount })}
          </span>
        </p>

        {/* Named, never dropped. Two of the historical measurements are a timer left open for
            seven weeks against a five-minute plan; an average that quietly discards them is a
            fiction, and one that quietly keeps them is worse. */}
        {outliers > 0 && (
          <p className="pl-5 text-muted-foreground">
            {t('postRecovery.lessonEvidenceOutliers',
              '{{n}} of those measurements are flagged as outliers — a task left open long past its plan, rather than a task that genuinely took that long.',
              { n: outliers })}
          </p>
        )}

        {coverage.runbookRevisions > 1 && (
          <p className="pl-5 text-muted-foreground">
            {t('postRecovery.lessonEvidenceRevisions',
              'The runbook has changed across those runs ({{n}} revisions), so the measurements are not all of the same plan.',
              { n: coverage.runbookRevisions })}
          </p>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl"
        // A stray click on the backdrop must not throw away a written comment. Escape still closes:
        // that is a deliberate gesture, and this dialog is not allowed to trap anyone.
        onInteractOutside={(e) => { if (dirty) e.preventDefault() }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {t('postRecovery.lessonWhy',
              "Your answers tune this object's runbook and its RTO for the next recovery.")}
          </DialogDescription>
        </DialogHeader>

        {requestId == null ? (
          <p className="py-4 text-sm text-muted-foreground">
            {t('postRecovery.lessonNoRun',
              'No recovery run is selected, so there is nothing to record feedback against yet.')}
          </p>
        ) : (
          <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
            {lesson.isPending && (
              <p className="text-xs text-muted-foreground">
                {t('postRecovery.lessonLoading', 'Looking for an answer already given for this run…')}
              </p>
            )}

            {lesson.isError && (
              <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 p-2.5 text-xs dark:bg-amber-950/40">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
                {/* Said out loud, because saving now would overwrite an answer nobody in this
                    session has seen. */}
                {t('postRecovery.lessonReadFailed',
                  'Any earlier answer for this run could not be read, so saving will replace whatever is stored.')}
              </p>
            )}

            {existing && (
              <p className="text-xs text-muted-foreground">
                {t('postRecovery.lessonReplaces',
                  'Answered on {{when}}. Saving replaces that answer rather than adding to it.',
                  { when: formatDate(existing.createdAt) })}
              </p>
            )}

            <Question<LessonAccuracy>
              label={t('postRecovery.lessonAccuracyQ', 'Was the runbook accurate?')}
              value={draft.accuracy}
              options={ACCURACY_OPTIONS.map((o) => ({ value: o.value, label: t(o.key, o.fallback) }))}
              onPick={(v) => setDraft((d) => ({ ...d, accuracy: v }))}
            />

            <Question<LessonTiming>
              label={t('postRecovery.lessonTimingQ', 'Was the timing accurate?')}
              hint={t('postRecovery.lessonTimingHint',
                'Too optimistic means the plan was shorter than the recovery actually took.')}
              value={draft.timing}
              options={TIMING_OPTIONS.map((o) => ({ value: o.value, label: t(o.key, o.fallback) }))}
              onPick={(v) => setDraft((d) => ({ ...d, timing: v }))}
            />

            <div>
              <Label htmlFor="lesson-rto" className="text-sm">
                {t('postRecovery.lessonRtoLabel', 'Proposed RTO for this object, in minutes')}
              </Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input
                  id="lesson-rto"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  className="w-32 tabular-nums"
                  value={draft.rto}
                  onChange={(e) => setDraft((d) => ({ ...d, rto: e.target.value }))}
                  placeholder={t('postRecovery.lessonRtoPlaceholder', 'minutes')}
                />
                {rtoValue !== null && !rtoInvalid && (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {t('postRecovery.lessonRtoEquals', '= {{d}}', { d: formatDurationMinutes(rtoValue) })}
                  </span>
                )}
              </div>
              {rtoInvalid && (
                <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                  {t('postRecovery.lessonRtoInvalid', 'Whole minutes, greater than zero.')}
                </p>
              )}
              {/* The field the tick above points at: someone who has just said the plan was wrong
                  is the one person who knows what it should have been. */}
              {!rtoInvalid && rtoRaw === '' && draft.timing !== '' && draft.timing !== 'about_right' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('postRecovery.lessonRtoPrompt', 'If you have a better number, this is where it goes.')}
                </p>
              )}
              <div className="mt-2">{renderEvidence()}</div>
            </div>

            <div>
              <Label htmlFor="lesson-comment" className="text-sm">
                {t('postRecovery.lessonCommentLabel', 'Anything else worth knowing? (optional)')}
              </Label>
              <Textarea
                id="lesson-comment"
                className="mt-1.5 min-h-[72px]"
                value={draft.comment}
                onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
                placeholder={t('postRecovery.lessonCommentPlaceholder',
                  'What surprised you, what was missing, what you would tell the next engineer.')}
              />
            </div>

            {/* Kept inline rather than in a toast: the dialog stays open on failure so the typed
                answer survives, and the reason it failed belongs beside it. */}
            {save.isError && (
              <p className="text-xs text-rose-600 dark:text-rose-400">
                {t('postRecovery.lessonSaveFailed', 'Not saved: {{reason}}', {
                  reason: handleApiError(save.error),
                })}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:items-center sm:justify-between">
          <p className="text-[11px] text-muted-foreground sm:mr-auto">
            {t('postRecovery.lessonOptionalHint',
              'Every answer is optional — click a chosen answer again to unset it.')}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={save.isPending}>
              {t('postRecovery.lessonSkip', 'Skip — nothing to add')}
            </Button>
            {requestId != null && (
              <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
                {save.isPending
                  ? t('common.saving', 'Saving…')
                  : existing
                    ? t('postRecovery.lessonUpdate', 'Update feedback')
                    : t('postRecovery.lessonSave', 'Save feedback')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
